import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { CardTile, money, StatusPill, METHOD_LABEL, useStoreCart } from "@/lib/giftStore";
import { ShoppingBag, Loader2, Copy, Check, Eye, EyeOff, Mail, Gift, Clock, ShieldCheck, ChevronDown, RotateCw } from "lucide-react";

function when(s) {
    if (!s) return "";
    try { return new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return s; }
}

function Timeline({ o }) {
    const paid = ["review", "paid", "awaiting_stock", "completed"].includes(o.status) || (o.status === "refunded" && o.paid_at);
    const steps = [
        { t: "Ordered", on: true, at: o.created_at },
        { t: o.status === "review" ? "Security check" : "Paid", on: paid, at: o.paid_at },
        { t: "Delivered", on: o.status === "completed", at: o.completed_at },
    ];
    return (
        <ol className="flex items-center gap-1 text-[11px] font-semibold text-slate-500">
            {steps.map((s, i) => (
                <li key={s.t} className="flex items-center gap-1 min-w-0">
                    <span className={`w-2.5 h-2.5 rounded-full ${s.on ? "bg-emerald-500" : "bg-slate-300"}`} />
                    <span className={`truncate ${s.on ? "text-slate-800" : ""}`}>{s.t}</span>
                    {i < steps.length - 1 && <span className={`w-5 sm:w-10 h-0.5 rounded ${steps[i + 1].on ? "bg-emerald-500" : "bg-slate-200"}`} />}
                </li>
            ))}
        </ol>
    );
}

function CodeBox({ code, pin }) {
    const [show, setShow] = useState(true);
    const [ok, setOk] = useState("");
    const copy = (v, k) => { navigator.clipboard?.writeText(v); setOk(k); setTimeout(() => setOk(""), 1500); };
    return (
        <div className="rounded-xl bg-slate-950 text-white p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
                <code className="font-mono text-base sm:text-lg tracking-wider break-all">{show ? code : "•".repeat(Math.min(code.length, 16))}</code>
                <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setShow((s) => !s)} aria-label={show ? "Hide code" : "Show code"} className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center">{show ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                    <button onClick={() => copy(code, "c")} aria-label="Copy code" className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-emerald-300">{ok === "c" ? <Check size={15} /> : <Copy size={15} />}</button>
                </div>
            </div>
            {pin && (
                <div className="flex items-center justify-between gap-2 border-t border-white/10 pt-2">
                    <span className="text-xs text-slate-400">PIN</span>
                    <span className="flex items-center gap-1"><code className="font-mono">{show ? pin : "••••"}</code>
                        <button onClick={() => copy(pin, "p")} aria-label="Copy PIN" className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-emerald-300">{ok === "p" ? <Check size={15} /> : <Copy size={15} />}</button></span>
                </div>
            )}
        </div>
    );
}

function OrderCard({ o, eta, focus, onChange }) {
    const [open, setOpen] = useState(focus);
    const [codes, setCodes] = useState(null);
    const [busy, setBusy] = useState("");
    const ref = useRef(null);
    useEffect(() => { if (focus && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "start" }); }, [focus]);

    const reveal = async () => {
        setBusy("reveal");
        try { const r = await api.post(`/store/orders/${o.id}/reveal`); setCodes(r.data.codes || []); }
        catch (e) { toast.error(e.response?.data?.detail || "Couldn't load your codes"); }
        finally { setBusy(""); }
    };
    const resend = async () => {
        setBusy("resend");
        try { await api.post(`/store/orders/${o.id}/resend`); toast.success(o.gift?.to_email ? `Sent again to ${o.gift.to_email}` : "Sent to your email"); }
        catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); }
        finally { setBusy(""); }
    };
    const iPaid = async () => {
        setBusy("paid");
        try { await api.post(`/store/orders/${o.id}/i-paid`); toast.success("Thanks! We'll confirm your payment shortly."); onChange(); }
        catch (e) { toast.error(e.response?.data?.detail || "Couldn't send that. Try again."); }
        finally { setBusy(""); }
    };
    const cancel = async () => {
        setBusy("cancel");
        try { await api.post(`/store/orders/${o.id}/cancel`); toast.success("Order cancelled"); onChange(); }
        catch (e) { toast.error(e.response?.data?.detail || "Couldn't cancel"); }
        finally { setBusy(""); }
    };
    const delivered = (o.lines || []).filter((l) => l.status === "delivered").length;
    const codeFor = (n) => (codes || []).find((c) => c.n === n);

    return (
        <article ref={ref} className={`rounded-3xl bg-white ring-1 ${focus ? "ring-2 ring-emerald-400" : "ring-slate-200"} overflow-hidden`}>
            <button onClick={() => setOpen((v) => !v)} className="w-full text-left p-4 sm:p-5 flex items-center gap-4">
                <div className="w-20 sm:w-24 shrink-0"><CardTile brand={o.lines?.[0]?.brand || o.lines?.[0]?.name} name={o.lines?.[0]?.name} color={o.lines?.[0]?.color || "#334155"} image={o.lines?.[0]?.image_url} size="sm" /></div>
                <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-bold text-slate-900">{o.lines?.length > 1 ? `${o.lines.length} gift cards` : o.lines?.[0]?.name}</span>
                        <StatusPill status={o.customer_paid_at && o.status === "pending_payment" ? "checking" : o.status} />
                    </div>
                    <div className="text-xs text-slate-500">{o.id} · {when(o.created_at)}{o.method ? ` · ${METHOD_LABEL[o.method] || o.method}` : ""}</div>
                    {!o.legacy && <Timeline o={o} />}
                </div>
                <div className="text-right shrink-0">
                    {o.total != null && <div className="font-extrabold text-slate-900">{money(o.currency, o.total)}</div>}
                    <ChevronDown size={18} className={`ml-auto mt-1 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                </div>
            </button>

            {open && (
                <div className="border-t border-slate-100 p-4 sm:p-5 space-y-4 bg-slate-50/50">
                    {o.status === "pending_payment" && o.method === "etransfer" && o.etransfer && (
                        <div className="rounded-2xl bg-sky-50 ring-1 ring-sky-100 p-4 text-sm text-sky-900 space-y-3">
                            <div>Send <b>{money(o.currency, o.etransfer.amount)}</b> by Interac e-Transfer to <b>{o.etransfer.email}</b> with <b>{o.id}</b> in the message.</div>
                            {o.customer_paid_at ? (
                                <div className="flex items-center gap-2 rounded-xl bg-white ring-1 ring-sky-100 px-3 py-2.5 font-semibold text-sky-900">
                                    <Clock size={16} className="text-sky-600" /> Thanks! We're checking your e-Transfer. Your code comes as soon as it arrives.
                                </div>
                            ) : (
                                <button onClick={iPaid} disabled={busy === "paid"} data-testid={`i-paid-${o.id}`}
                                    className="h-12 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold inline-flex items-center gap-2">
                                    {busy === "paid" ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} I've sent the e-Transfer
                                </button>
                            )}
                        </div>
                    )}
                    {o.status === "pending_payment" && o.method !== "etransfer" && (
                        <div className="rounded-2xl bg-amber-50 ring-1 ring-amber-100 p-4 text-sm text-amber-900">We haven't received this payment yet. If you paid, it can take a minute to show.</div>
                    )}
                    {o.status === "review" && (
                        <div className="rounded-2xl bg-violet-50 ring-1 ring-violet-100 p-4 text-sm text-violet-900 flex gap-2"><ShieldCheck size={18} className="shrink-0" />Your payment is in. We do a quick security check on some orders to keep your account safe. Your code will follow shortly.</div>
                    )}
                    {o.status === "awaiting_stock" && (
                        <div className="rounded-2xl bg-sky-50 ring-1 ring-sky-100 p-4 text-sm text-sky-900 flex gap-2"><Clock size={18} className="shrink-0" />Paid. We're preparing your code. It usually arrives {eta}. We'll email you.</div>
                    )}
                    {o.status === "refunded" && o.refund && (
                        <div className="rounded-2xl bg-rose-50 ring-1 ring-rose-100 p-4 text-sm text-rose-900">Refunded {o.refund.how === "wallet" ? "to your wallet" : o.refund.how === "card" ? "to your card (3-10 business days)" : ""}.</div>
                    )}
                    {o.gift?.to_email && (
                        <div className="flex items-center gap-2 text-sm text-slate-600"><Gift size={16} className="text-pink-500" /> Gift for {o.gift.to_name || o.gift.to_email}{o.status === "completed" ? " · emailed to them" : ""}</div>
                    )}

                    <ul className="space-y-3">
                        {(o.lines || []).map((l) => (
                            <li key={l.n} className="rounded-2xl bg-white ring-1 ring-slate-200 p-3 sm:p-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-semibold text-slate-900 truncate">{l.name}{l.value ? ` · ${money(o.currency, l.value)}` : ""}</div>
                                        <div className="text-xs text-slate-500">{l.status === "delivered" ? (o.gift?.to_email ? `Emailed to ${o.gift.to_name || o.gift.to_email}` : l.last4 ? `Code ending …${l.last4}` : "Delivered") : "Not delivered yet"}</div>
                                    </div>
                                    {l.status === "delivered" ? <span className="text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-1">Ready</span> : <span className="text-xs font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-1">Waiting</span>}
                                </div>
                                {codeFor(l.n)?.code && <div className="mt-3"><CodeBox code={codeFor(l.n).code} pin={codeFor(l.n).pin} /></div>}
                                {codeFor(l.n)?.code && l.how_to_redeem && <p className="mt-2 text-xs text-slate-500 whitespace-pre-line">{l.how_to_redeem}</p>}
                            </li>
                        ))}
                    </ul>

                    <div className="flex flex-wrap gap-2">
                        {delivered > 0 && !codes && !o.gift?.to_email && (
                            <button onClick={reveal} disabled={busy === "reveal"} data-testid={`reveal-${o.id}`} className="h-11 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center gap-2">
                                {busy === "reveal" ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />} Show my code{delivered > 1 ? "s" : ""}
                            </button>
                        )}
                        {o.status === "completed" && !o.legacy && (
                            <button onClick={resend} disabled={busy === "resend"} className="h-11 px-4 rounded-xl ring-1 ring-slate-200 bg-white font-bold text-slate-700 flex items-center gap-2">
                                {busy === "resend" ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} {o.gift?.to_email ? "Email the gift again" : "Email me again"}
                            </button>
                        )}
                        {o.status === "pending_payment" && !o.legacy && (
                            <button onClick={cancel} disabled={busy === "cancel"} className="h-11 px-4 rounded-xl ring-1 ring-slate-200 bg-white font-bold text-slate-700">Cancel order</button>
                        )}
                    </div>
                    {codes && <p className="text-[11px] text-slate-400">Keep your codes private. Anyone with a code can spend it. We never ask for your code by phone or chat.</p>}
                </div>
            )}
        </article>
    );
}

export default function MyGiftCardOrders() {
    const [params, setParams] = useSearchParams();
    const focusId = params.get("order");
    const [data, setData] = useState(null);
    const [checking, setChecking] = useState(false);
    const [showOld, setShowOld] = useState(false);
    const { clear } = useStoreCart();

    const load = useCallback(() => api.get("/store/my-orders").then((r) => setData(r.data)).catch(() => setData({ orders: [], legacy: [] })), []);

    // Back from Stripe or PayPal: confirm the payment, then keep checking for a short while
    useEffect(() => {
        const paid = params.get("paid");
        if (!focusId || !paid) { load(); return; }
        let stop = false;
        (async () => {
            setChecking(true);
            try {
                if (paid === "paypal" && params.get("paymentId") && params.get("PayerID")) {
                    await api.post(`/store/orders/${focusId}/paypal`, { payment_id: params.get("paymentId"), payer_id: params.get("PayerID") });
                }
                for (let i = 0; i < 8 && !stop; i++) {
                    const r = await api.post(`/store/orders/${focusId}/confirm`);
                    const st = r.data?.order?.status;
                    if (st && st !== "pending_payment") {
                        if (r.data.order.currency) clear(r.data.order.currency);
                        if (st === "completed") toast.success("Payment received. Your gift card is ready!");
                        break;
                    }
                    await new Promise((res) => setTimeout(res, 2500));
                }
            } catch (e) {
                toast.error(e.response?.data?.detail || "We couldn't confirm the payment yet. We'll keep checking.");
            } finally {
                if (!stop) {
                    setChecking(false);
                    await load();
                    const p = new URLSearchParams(); p.set("order", focusId); setParams(p, { replace: true });
                }
            }
        })();
        return () => { stop = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const orders = data?.orders || [];
    const legacy = data?.legacy || [];
    const stats = {
        cards: orders.filter((o) => o.status === "completed").reduce((s, o) => s + (o.lines?.length || 0), 0),
        waiting: orders.filter((o) => ["pending_payment", "review", "awaiting_stock", "paid"].includes(o.status)).length,
    };

    return (
        <div className="space-y-5 w-full min-w-0">
            <div className="rounded-3xl bg-[#070b14] text-white p-5 sm:p-6 relative overflow-hidden">
                <div className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(400px 200px at 90% 0%, rgba(16,185,129,0.35), transparent 70%)" }} />
                <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-extrabold" style={{ fontFamily: "Outfit, ui-sans-serif" }}>My gift cards</h1>
                        <p className="text-sm text-slate-300">Codes you bought, ready to use or send.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="rounded-2xl bg-white/10 px-4 py-2"><div className="text-xs text-slate-300">Delivered</div><div className="text-xl font-extrabold">{stats.cards}</div></div>
                        <div className="rounded-2xl bg-white/10 px-4 py-2"><div className="text-xs text-slate-300">In progress</div><div className="text-xl font-extrabold">{stats.waiting}</div></div>
                        <Link to="/buy-giftcard" className="h-11 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold flex items-center gap-2"><ShoppingBag size={16} /> Buy</Link>
                    </div>
                </div>
            </div>

            {checking && (
                <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 flex items-center gap-3 text-sm text-slate-700"><Loader2 size={18} className="animate-spin text-emerald-600" /> Confirming your payment...</div>
            )}

            {!data ? (
                <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-emerald-600" /></div>
            ) : orders.length === 0 && legacy.length === 0 ? (
                <div className="rounded-3xl bg-white ring-1 ring-slate-200 p-12 text-center">
                    <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Gift size={24} /></div>
                    <h2 className="mt-4 text-xl font-bold text-slate-900">No gift cards yet</h2>
                    <p className="mt-1 text-sm text-slate-500">Buy one for yourself or send one to a friend.</p>
                    <Link to="/buy-giftcard" className="mt-5 inline-flex h-11 px-5 rounded-xl bg-emerald-600 text-white font-bold items-center">Browse gift cards</Link>
                </div>
            ) : (
                <>
                    <div className="flex justify-end"><button onClick={load} className="text-sm font-semibold text-slate-500 hover:text-slate-800 flex items-center gap-1.5"><RotateCw size={14} /> Refresh</button></div>
                    <div className="space-y-3">
                        {orders.map((o) => <OrderCard key={o.id} o={o} eta={data.manual_eta || "soon"} focus={o.id === focusId} onChange={load} />)}
                    </div>
                    {legacy.length > 0 && (
                        <div>
                            <button onClick={() => setShowOld((v) => !v)} className="text-sm font-semibold text-slate-600 flex items-center gap-1.5">
                                Older orders ({legacy.length}) <ChevronDown size={15} className={showOld ? "rotate-180" : ""} />
                            </button>
                            {showOld && <div className="mt-3 space-y-3">{legacy.map((o) => <OrderCard key={o.id} o={o} eta="soon" focus={false} onChange={load} />)}</div>}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
